import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  PageHeader,
  StateText,
} from '../../components/Foundation';

import {
  EmptyState,
  LoadingLine,
} from '../../components/State';

import {
  getLidUi,
  lidBilingual,
} from './lidUi';

import {
  STATIC_QUESTION_TRANSLATION_PAIRS,
} from './lidSemanticOverrides';

import {
  GENERATED_QUESTION_TRANSLATION_PAIRS,
} from './lidSemanticGenerated';

import {
  LiDLearningBridge,
  type LearningBridgeResult,
} from './LiDLearningBridge';
import { buildHumanLearningModel, type HumanLearningModel } from './lidHumanLearning';

import {
  useGermanSpeech,
  type SpeechRate,
} from './useGermanSpeech';


export type LiDMode =
  | 'practice'
  | 'exam'
  | 'wrong'
  | 'favorites'
  | 'progress';


interface TextBlock {
  text: string;
  options: Array<{
    key: string;
    text: string;
  }>;
  explanation?: string | null;
}


interface TranslationBlock
  extends TextBlock {
  language: string;
  status: string;
  direction: 'ltr' | 'rtl';
}


interface Question {
  id: string;
  question_key: string;
  canonical: TextBlock;
  translation: TranslationBlock | null;
  favorite: boolean;
  asset_key: string | null;
}


const GENERAL_VISUAL_QUESTION_NUMBERS = new Set([
  21,
  55,
  70,
  130,
  176,
  181,
  187,
  209,
  216,
  226,
  235,
]);


function lidVisualUrl(
  questionKey: string,
): string | null {
  const parts = questionKey.split(':');

  if (
    parts.length === 4
    && parts[0] === 'lid'
    && parts[1] === 'general'
    && parts[2] === 'DE'
  ) {
    const number = Number(parts[3]);

    if (GENERAL_VISUAL_QUESTION_NUMBERS.has(number)) {
      return `/lid-assets/general-DE-${number}.png`;
    }
  }

  if (
    parts.length === 4
    && parts[0] === 'lid'
    && parts[1] === 'state'
    && (parts[3] === '1' || parts[3] === '8')
  ) {
    return `/lid-assets/state-${parts[2]}-${parts[3]}.png`;
  }

  return null;
}


interface AttemptResult {
  attempt_id?: string | null;
  correct: boolean;
  correct_option?: string | null;
  explanation?: string | null;
  learning_bridge?: LearningBridgeResult | null;
}


interface Props {
  bundesland: string;
  language: string;
  mode: Exclude<LiDMode, 'progress'>;
  onProgressChanged?: () => void;
}


type LearningMarkKind =
  | 'trap'
  | 'shared'
  | 'signal'
  | 'question-focus'
  | 'correct-concept'
  | 'distractor';


function normalizeLearningToken(
  value: string,
): string {
  return value
    .toLocaleLowerCase('de-DE')
    .replace(/[^a-zäöüß]/giu, '');
}


function sharesLearningRoot(
  left: string,
  right: string,
): boolean {
  const a = normalizeLearningToken(left);
  const b = normalizeLearningToken(right);

  if (!a || !b) {
    return false;
  }

  if (a === b) {
    return true;
  }

  if (Math.min(a.length, b.length) < 5) {
    return false;
  }

  return a.startsWith(b)
    || b.startsWith(a);
}


function learningMarkKind(
  value: string,
  model: HumanLearningModel,
): LearningMarkKind | null {
  const token = normalizeLearningToken(value);

  if (!token) {
    return null;
  }

  if (
    model.trapTerms.some(item =>
      normalizeLearningToken(item.word)
      === token
    )
  ) {
    return 'trap';
  }

  if (
    model.sharedTerms.some(item =>
      sharesLearningRoot(
        item.german,
        value,
      )
    )
  ) {
    return 'shared';
  }

  const signalParts = model.signal
    .split(/\s+/u);

  if (
    signalParts.some(item =>
      sharesLearningRoot(item, value)
    )
  ) {
    return 'signal';
  }

  return null;
}


interface SemanticHighlights {
  questionFocus?: string | null;
  correctEvidence?: string | null;
  distractorAnswer?: string | null;
  distractorTone?: 'a' | 'b' | null;
}


const SEMANTIC_STOP_WORDS = new Set([
  'aber',
  'alle',
  'auch',
  'dann',
  'dass',
  'denn',
  'deutschland',
  'dürfen',
  'eine',
  'einer',
  'eines',
  'gegen',
  'haben',
  'hier',
  'kann',
  'können',
  'muss',
  'müssen',
  'nicht',
  'offen',
  'sagen',
  'weil',
  'wenn',
  'werden',
]);


function semanticConceptWords(
  text: string | null | undefined,
  model: HumanLearningModel,
): string[] {
  if (!text) {
    return [];
  }

  const shared = new Set(
    model.sharedTerms.map(item =>
      normalizeLearningToken(item.german)
    )
  );

  return Array.from(
    new Set(
      (
        text.match(
          /\b[A-ZÄÖÜ][A-Za-zÄÖÜäöüß-]{3,}\b/g
        )
        || []
      )
        .map(normalizeLearningToken)
        .filter(Boolean)
        .filter(word =>
          !shared.has(word)
          && !SEMANTIC_STOP_WORDS.has(word)
        )
    )
  );
}



function answerStructure(
  text: string,
): string | null {
  const normalized = text.trim();

  if (
    /^hier\b.+\bgilt[.!?]?$/iu.test(
      normalized,
    )
  ) {
    return 'hier … gilt';
  }

  if (
    /^hier\b.+\bsind[.!?]?$/iu.test(
      normalized,
    )
  ) {
    return 'hier … sind';
  }

  return null;
}


function recurringAnswerStructure(
  text: string,
  options: string[],
): string | null {
  const structure = answerStructure(text);

  if (!structure) {
    return null;
  }

  const count = options.filter(
    option =>
      answerStructure(option) === structure
  ).length;

  return count >= 2
    ? structure
    : null;
}


type ConceptFamily =
  | 'rights'
  | 'institution'
  | 'state'
  | 'history'
  | 'society';


function conceptFamily(
  text: string | null | undefined,
): ConceptFamily | null {
  if (!text) {
    return null;
  }

  if (
    /\b(?:Meinungsfreiheit|Religionsfreiheit|Pressefreiheit|Versammlungsfreiheit|Wahlrecht|Grundrecht|Grundgesetz|Menschenwürde|Gleichberechtigung)\w*\b/iu
      .test(text)
  ) {
    return 'rights';
  }

  if (
    /\b(?:Bundestag|Bundesrat|Bundesregierung|Bundeskanzler|Bundespräsident|Parlament|Gericht|Polizei|Partei|Koalition|Opposition)\w*\b/iu
      .test(text)
  ) {
    return 'institution';
  }

  if (
    /\b(?:Bundesland|Landtag|Gemeinde|Ministerpräsident|Föderalismus)\w*\b/iu
      .test(text)
  ) {
    return 'state';
  }

  if (
    /\b(?:Nationalsozialismus|DDR|Mauer|Wiedervereinigung|Holocaust|Europäische Union)\w*\b/iu
      .test(text)
  ) {
    return 'history';
  }

  if (
    /\b(?:Gewerkschaft|Betriebsrat|Schule|Religion|Familie|Integration|Verein|Asyl|Sozialversicherung|Arbeitslosigkeit)\w*\b/iu
      .test(text)
  ) {
    return 'society';
  }

  return null;
}


function isNearWrongConcept(
  optionText: string,
  correctText: string | null,
): boolean {
  const optionFamily =
    conceptFamily(optionText);

  const correctFamily =
    conceptFamily(correctText);

  return Boolean(
    optionFamily
    && correctFamily
    && optionFamily === correctFamily
    && optionText !== correctText
  );
}


const LEARNING_SEEN_KEY =
  'lid.human-learning.scaffold.seen';


function readLearningSeen(): string[] {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(
        LEARNING_SEEN_KEY
      )
      || '[]'
    );

    return Array.isArray(parsed)
      ? parsed.filter(
          value => typeof value === 'string'
        )
      : [];
  } catch {
    return [];
  }
}


function markLearningSeen(
  questionKey: string,
): number {
  try {
    const seen = readLearningSeen();

    if (!seen.includes(questionKey)) {
      seen.push(questionKey);

      localStorage.setItem(
        LEARNING_SEEN_KEY,
        JSON.stringify(seen.slice(-500)),
      );
    }

    return seen.length;
  } catch {
    return 0;
  }
}


function countRecognizedPatterns(): number {
  try {
    const prefix =
      'lid.human-learning.pattern-mastery.';

    let count = 0;

    for (
      let index = 0;
      index < localStorage.length;
      index += 1
    ) {
      const key =
        localStorage.key(index);

      if (
        !key
        || !key.startsWith(prefix)
      ) {
        continue;
      }

      const value =
        Number(
          localStorage.getItem(key)
          || 0
        );

      // "recognized" requires more than
      // one successful net exposure.
      if (value >= 2) {
        count += 1;
      }
    }

    return count;
  } catch {
    return 0;
  }
}



function buildA1QuestionChunks(
  text: string,
): string[] {
  const clean = text.trim();

  const connectorMatch =
    clean.match(
      /\b(?:weil|dass|wenn|obwohl)\b/iu
    );

  let before = clean;
  let connectorPart = '';

  if (
    connectorMatch
    && connectorMatch.index !== undefined
  ) {
    before = clean
      .slice(0, connectorMatch.index)
      .replace(/[,\s]+$/u, '')
      .trim();

    connectorPart = clean
      .slice(connectorMatch.index)
      .trim();
  }

  const chunks: string[] = [];

  const modalMatch =
    before.match(
      /\b(?:dürfen|darf|müssen|muss|können|kann|sollen|soll)\b/iu
    );

  if (
    modalMatch
    && modalMatch.index !== undefined
    && modalMatch.index > 0
  ) {
    const opening =
      before
        .slice(0, modalMatch.index)
        .trim();

    const remainder =
      before
        .slice(modalMatch.index)
        .trim();

    if (opening) {
      chunks.push(opening);
    }

    const modalSubject =
      remainder.match(
        /^(\S+\s+\S+)(?:\s+(.+))?$/u
      );

    if (modalSubject) {
      chunks.push(modalSubject[1]);

      if (modalSubject[2]) {
        chunks.push(modalSubject[2]);
      }
    } else if (remainder) {
      chunks.push(remainder);
    }
  } else if (before) {
    chunks.push(before);
  }

  if (connectorPart) {
    chunks.push(connectorPart);
  }

  const usable =
    chunks
      .map(item => item.trim())
      .filter(Boolean)
      .slice(0, 4);

  return usable.length > 1
    ? usable
    : [clean];
}


interface FreedomCompoundInsight {
  word: string;
  base: string;
  linker: string | null;
}


function freedomCompoundInsight(
  text: string | null | undefined,
): FreedomCompoundInsight | null {
  if (!text) {
    return null;
  }

  const match =
    text.match(
      /\b([A-ZÄÖÜ][A-Za-zÄÖÜäöüß]*freiheit)\b/iu
    );

  if (!match) {
    return null;
  }

  const word = match[1];

  const prefix =
    word.slice(
      0,
      word.length - 'freiheit'.length,
    );

  if (!prefix) {
    return null;
  }

  const hasLinkingS =
    prefix.length > 2
    && prefix.endsWith('s');

  return {
    word,
    base:
      hasLinkingS
        ? prefix.slice(0, -1)
        : prefix,
    linker:
      hasLinkingS
        ? 's'
        : null,
  };
}


function finalGermanWord(
  text: string | null | undefined,
): string | null {
  if (!text) {
    return null;
  }

  return (
    text
      .replace(/[.!?…]+$/u, '')
      .trim()
      .match(
        /([A-Za-zÄÖÜäöüß-]+)$/u
      )
      ?.[1]
    || null
  );
}


type A1EliminationKind =
  | 'near'
  | 'structure'
  | 'unrelated-rights'
  | 'unrelated';


function a1EliminationKind(
  optionText: string,
  correctText: string | null,
  optionTexts: string[],
): A1EliminationKind {
  if (
    isNearWrongConcept(
      optionText,
      correctText,
    )
  ) {
    return 'near';
  }

  if (
    recurringAnswerStructure(
      optionText,
      optionTexts,
    )
  ) {
    return 'structure';
  }

  if (
    conceptFamily(correctText) === 'rights'
    && !conceptFamily(optionText)
  ) {
    return 'unrelated-rights';
  }

  return 'unrelated';
}


function A1ConceptIcon({
  kind,
}: {
  kind: string;
}) {
  if (kind === 'rights') {
    return (
      <svg
        viewBox="0 0 48 48"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M10 9h28v20H24l-9 8v-8h-5V9Z" />
        <path d="M16 16h16M16 21h11" />
      </svg>
    );
  }

  if (kind === 'institution') {
    return (
      <svg
        viewBox="0 0 48 48"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M8 18 24 9l16 9M11 20h26M14 23v13M22 23v13M30 23v13M38 39H10" />
      </svg>
    );
  }

  return (
    <svg
      viewBox="0 0 48 48"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="24" cy="24" r="14" />
      <path d="M17 24h14M24 17v14" />
    </svg>
  );
}


function renderLearningText(
  text: string,
  model: HumanLearningModel,
  semantic: SemanticHighlights = {},
): React.ReactNode {
  const parts = text.split(
    /(\s+|[.,!?;:()„“"…/]+)/u
  );

  const focusWords =
    semanticConceptWords(
      semantic.questionFocus,
      model,
    );

  const correctWords =
    semanticConceptWords(
      semantic.correctEvidence,
      model,
    );

  const distractorWords =
    semanticConceptWords(
      semantic.distractorAnswer,
      model,
    );

  return parts.map((part, index) => {
    const normalized =
      normalizeLearningToken(part);

    const semanticKind =
      distractorWords.includes(normalized)
        ? 'distractor'
        : correctWords.includes(normalized)
          ? 'correct-concept'
          : focusWords.includes(normalized)
            ? 'question-focus'
            : null;

    const kind =
      semanticKind
      || learningMarkKind(
        part,
        model,
      );

    if (!kind) {
      return (
        <React.Fragment key={`${part}-${index}`}>
          {part}
        </React.Fragment>
      );
    }

    const toneClass =
      kind === 'distractor'
      && semantic.distractorTone
        ? ` is-distractor-tone-${semantic.distractorTone}`
        : '';

    return (
      <mark
        key={`${part}-${index}`}
        className={`lid-learning-mark is-${kind}${toneClass}`}
      >
        {part}
      </mark>
    );
  });
}


function lidStaticAudioUrl(
  questionKey: string,
  kind: 'question' | 'A' | 'B' | 'C' | 'D',
): string | null {
  const general =
    questionKey.match(
      /^lid:general:DE:(.+)$/,
    );

  if (general) {
    return (
      '/lid-audio/de/general/'
      + `${general[1]}/${kind}.opus`
    );
  }

  const state =
    questionKey.match(
      /^lid:state:([A-Z]{2}):(.+)$/,
    );

  if (state) {
    return (
      '/lid-audio/de/state/'
      + `${state[1]}/${state[2]}/${kind}.opus`
    );
  }

  return null;
}



const SH_PILOT_BACKGROUNDS = [
  {
    id: 'ST-SH-001',
    url: '/lid-visuals/optimized/states/SH/ST-SH-001.webp',
    position: '50% 54%',
  },
  {
    id: 'ST-SH-002',
    url: '/lid-visuals/optimized/states/SH/ST-SH-002.webp',
    position: '52% 66%',
  },
  {
    id: 'ST-SH-003',
    url: '/lid-visuals/optimized/states/SH/ST-SH-003.webp',
    position: '49% 53%',
  },
  {
    id: 'ST-SH-004',
    url: '/lid-visuals/optimized/states/SH/ST-SH-004.webp',
    position: '60% 56%',
  },
  {
    id: 'ST-SH-005',
    url: '/lid-visuals/optimized/states/SH/ST-SH-005.webp',
    position: '53% 57%',
  },
] as const;

function lidShPilotBackground(
  questionKey: string,
) {
  const match = questionKey.match(
    /^lid:state:SH:(.+)$/,
  );

  if (!match) {
    return null;
  }

  let hash = 2166136261;

  for (const character of match[1]) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }

  return SH_PILOT_BACKGROUNDS[
    (hash >>> 0) % SH_PILOT_BACKGROUNDS.length
  ];
}

function isImageNumberOption(
  questionKey: string,
  text: string,
): boolean {
  return (
    Boolean(lidVisualUrl(questionKey))
    && /^\d+$/.test(text.trim())
  );
}



type SpeechSegmenter = {
  segment: (
    input: string
  ) => {
    readonly length: number;
    readonly [index: number]: {
      segment: string;
    };
  };
};

type SpeechSegmenterConstructor = new (
  locales?: string | string[],
  options?: {
    granularity: 'grapheme';
  },
) => SpeechSegmenter;

function splitSpeechGraphemes(
  text: string,
): string[] {
  const SegmenterCtor = (
    Intl as unknown as {
      Segmenter?: SpeechSegmenterConstructor;
    }
  ).Segmenter;

  if (SegmenterCtor) {
    const segmenter =
      new SegmenterCtor(
        'de',
        { granularity: 'grapheme' },
      );

    return Array.from(
      segmenter.segment(text),
      item => item.segment,
    );
  }

  return Array.from(text);
}

function renderSpeechTrackedText(
  text: string,
  progress: number,
) {
  const graphemes =
    splitSpeechGraphemes(text);

  const normalized =
    Math.max(
      0,
      Math.min(progress, 1),
    );

  const readCount =
    Math.min(
      graphemes.length,
      Math.floor(
        graphemes.length * normalized
      ),
    );

  const read =
    graphemes
      .slice(0, readCount)
      .join('');

  const unread =
    graphemes
      .slice(readCount)
      .join('');

  return (
    <span
      className="lid-speech-tracked-text"
      aria-label={text}
    >
      <span
        className="lid-speech-read"
        aria-hidden="true"
      >
        {read}
      </span>

      <span
        className="lid-speech-unread"
        aria-hidden="true"
      >
        {unread}
      </span>
    </span>
  );
}



type QuestionTranslationTone =
  | 'shared'
  | 'signal'
  | 'trap'
  | 'semantic';

interface QuestionTranslationPair {
  german: string;
  translated: string;
  tone: QuestionTranslationTone;
}




/*
 * KNSAIX_LID_CANONICAL_RANGE_RENDERER_V1
 *
 * Production semantic mappings already contain literal,
 * corpus-validated German/translated spans.
 *
 * Rendering therefore places those spans directly instead
 * of rebuilding semantic boundaries with one alternation
 * regex. Pair indexes remain authoritative on both sides.
 */

function normalizeQuestionPair(
  value: string,
): string {
  return value
    .normalize('NFKC')
    .trim()
    .toLocaleLowerCase();
}

function staticQuestionTranslationPairs(
  questionKey: string | null | undefined,
  language: string | null | undefined,
): QuestionTranslationPair[] {
  if (!questionKey || !language) {
    return [];
  }

  const key =
    `${questionKey}|${language}`;

  const generated =
    GENERATED_QUESTION_TRANSLATION_PAIRS[key]
    || [];

  const manual =
    STATIC_QUESTION_TRANSLATION_PAIRS[key]
    || [];

  if (generated.length === 0) {
    return manual;
  }

  if (manual.length === 0) {
    return generated;
  }

  /*
   * Generated production mappings are authoritative.
   * Static mappings remain a supplement for legacy/golden
   * cases, but may not replace an already generated German
   * semantic unit.
   */
  const merged: QuestionTranslationPair[] = [
    ...generated,
  ];

  const seenExact =
    new Set(
      generated.map(
        pair =>
          normalizeQuestionPair(pair.german)
          + '\u0000'
          + normalizeQuestionPair(
            pair.translated,
          ),
      ),
    );

  const seenGerman =
    new Set(
      generated.map(
        pair =>
          normalizeQuestionPair(
            pair.german,
          ),
      ),
    );

  for (const pair of manual) {
    const german =
      normalizeQuestionPair(
        pair.german,
      );

    const exact =
      german
      + '\u0000'
      + normalizeQuestionPair(
        pair.translated,
      );

    if (seenExact.has(exact)) {
      continue;
    }

    if (seenGerman.has(german)) {
      continue;
    }

    /*
     * KNSAIX_LID_STATIC_SUPPLEMENT_NONOVERLAP_V1
     *
     * Generated mappings are authoritative. A legacy/static
     * supplement may only be added when it does not overlap
     * or nest inside an already accepted semantic span on
     * either the German or translated side.
     */
    const overlapsExisting =
      merged.some(
        existing => {
          const existingGerman =
            normalizeQuestionPair(
              existing.german,
            );

          const existingTranslated =
            normalizeQuestionPair(
              existing.translated,
            );

          const translated =
            normalizeQuestionPair(
              pair.translated,
            );

          const germanOverlap =
            existingGerman.includes(
              german,
            )
            || german.includes(
              existingGerman,
            );

          const translatedOverlap =
            existingTranslated.includes(
              translated,
            )
            || translated.includes(
              existingTranslated,
            );

          return (
            germanOverlap
            || translatedOverlap
          );
        },
      );

    if (overlapsExisting) {
      continue;
    }

    merged.push(pair);
    seenExact.add(exact);
    seenGerman.add(german);
  }

  return merged;
}

function buildQuestionTranslationPairs(
  _model: HumanLearningModel,
  questionKey?: string,
  language?: string,
): QuestionTranslationPair[] {
  return staticQuestionTranslationPairs(
    questionKey,
    language,
  );
}

interface QuestionPairOccurrence {
  start: number;
  end: number;
}

interface PositionedQuestionPair
  extends QuestionPairOccurrence {
  pairIndex: number;
  tone: QuestionTranslationTone;
}

function questionPairOccurrences(
  text: string,
  rawValue: string,
): QuestionPairOccurrence[] {
  const value = rawValue.trim();

  if (!value) {
    return [];
  }

  const escaped =
    value.replace(
      /[.*+?^${}()|[\]\\]/g,
      '\\$&',
    );

  const expression =
    new RegExp(
      escaped,
      'giu',
    );

  const occurrences:
    QuestionPairOccurrence[] = [];

  for (
    let match = expression.exec(text);
    match;
    match = expression.exec(text)
  ) {
    occurrences.push({
      start: match.index,
      end:
        match.index
        + match[0].length,
    });

    if (match[0].length === 0) {
      expression.lastIndex += 1;
    }
  }

  return occurrences;
}

function questionPairRanges(
  text: string,
  pairs: QuestionTranslationPair[],
  side: 'german' | 'translated',
): PositionedQuestionPair[] {
  const candidates =
    pairs
      .map((pair, pairIndex) => {
        const value =
          side === 'german'
            ? pair.german
            : pair.translated;

        return {
          pairIndex,
          tone: pair.tone,
          value,
          occurrences:
            questionPairOccurrences(
              text,
              value,
            ),
        };
      })
      .filter(
        candidate =>
          candidate.value
            .trim()
            .length > 0,
      );

  if (candidates.length === 0) {
    return [];
  }

  /*
   * Fewest possible positions first keeps the search tiny
   * and deterministic. Longer spans win ties.
   */
  const searchOrder =
    [...candidates].sort(
      (a, b) =>
        a.occurrences.length
        - b.occurrences.length
        || b.value.trim().length
        - a.value.trim().length
        || a.pairIndex
        - b.pairIndex,
    );

  if (
    searchOrder.some(
      candidate =>
        candidate.occurrences.length
        === 0,
    )
  ) {
    return [];
  }

  const selected =
    new Map<
      number,
      QuestionPairOccurrence
    >();

  const used:
    QuestionPairOccurrence[] = [];

  const overlaps = (
    candidate: QuestionPairOccurrence,
  ): boolean =>
    used.some(
      current =>
        candidate.start
          < current.end
        && candidate.end
          > current.start,
    );

  const place = (
    position: number,
  ): boolean => {
    if (
      position
      >= searchOrder.length
    ) {
      return true;
    }

    const candidate =
      searchOrder[position];

    for (
      const occurrence
      of candidate.occurrences
    ) {
      if (overlaps(occurrence)) {
        continue;
      }

      selected.set(
        candidate.pairIndex,
        occurrence,
      );

      used.push(occurrence);

      if (place(position + 1)) {
        return true;
      }

      used.pop();

      selected.delete(
        candidate.pairIndex,
      );
    }

    return false;
  };

  if (!place(0)) {
    /*
     * Never invent or mis-colour a relationship.
     * Build-time global acceptance guarantees production
     * mappings do not reach this fallback.
     */
    return [];
  }

  return candidates
    .map(candidate => {
      const occurrence =
        selected.get(
          candidate.pairIndex,
        );

      if (!occurrence) {
        return null;
      }

      return {
        ...occurrence,
        pairIndex:
          candidate.pairIndex,
        tone:
          candidate.tone,
      };
    })
    .filter(
      (
        range,
      ): range is PositionedQuestionPair =>
        range !== null,
    )
    .sort(
      (a, b) =>
        a.start - b.start
        || a.end - b.end
        || a.pairIndex
        - b.pairIndex,
    );
}

function renderQuestionPairText(
  text: string,
  model: HumanLearningModel,
  side: 'german' | 'translated',
  questionKey?: string,
  language?: string,
): React.ReactNode {
  const pairs =
    buildQuestionTranslationPairs(
      model,
      questionKey,
      language,
    );

  const ranges =
    questionPairRanges(
      text,
      pairs,
      side,
    );

  if (ranges.length === 0) {
    return text;
  }

  const nodes:
    React.ReactNode[] = [];

  let cursor = 0;

  ranges.forEach(
    (range, rangeIndex) => {
      if (range.start > cursor) {
        nodes.push(
          <React.Fragment
            key={
              `plain-${cursor}-${range.start}`
            }
          >
            {text.slice(
              cursor,
              range.start,
            )}
          </React.Fragment>,
        );
      }

      nodes.push(
        <mark
          key={
            `pair-${range.pairIndex}-${rangeIndex}`
          }
          className={
            `lid-question-pair-mark is-${range.tone} `
            + `is-pair-${range.pairIndex % 10}`
          }
          data-pair-index={
            range.pairIndex
          }
        >
          {text.slice(
            range.start,
            range.end,
          )}
        </mark>,
      );

      cursor = range.end;
    },
  );

  if (cursor < text.length) {
    nodes.push(
      <React.Fragment
        key={
          `plain-${cursor}-${text.length}`
        }
      >
        {text.slice(cursor)}
      </React.Fragment>,
    );
  }

  return nodes;
}


function renderQuestionTranslationText(
  text: string,
  model: HumanLearningModel,
  questionKey?: string,
  language?: string,
): React.ReactNode {
  return renderQuestionPairText(
    text,
    model,
    'translated',
    questionKey,
    language,
  );
}

function renderQuestionGermanPairText(
  text: string,
  model: HumanLearningModel,
  questionKey?: string,
  language?: string,
): React.ReactNode {
  return renderQuestionPairText(
    text,
    model,
    'german',
    questionKey,
    language,
  );
}

export function LiDQuestionFlow({
  bundesland,
  language,
  mode,
  onProgressChanged,
}: Props) {
  const [questions, setQuestions] =
    useState<Question[]>([]);

  const [currentIndex, setCurrentIndex] =
    useState(0);

  const [answers, setAnswers] =
    useState<Record<string, string>>({});

  const [results, setResults] =
    useState<Record<string, AttemptResult>>({});

  const [loading, setLoading] =
    useState(true);

  const [submitting, setSubmitting] =
    useState(false);

  const [error, setError] =
    useState<string | null>(null);

  const [examFinished, setExamFinished] =
    useState(false);

  const [learningSeenCount, setLearningSeenCount] =
    useState(
      () => readLearningSeen().length
    );

  const [recognizedPatterns, setRecognizedPatterns] =
    useState(
      () => countRecognizedPatterns()
    );

  const [
    activeLegend,
    setActiveLegend,
  ] = useState<
    'shared'
    | 'correct'
    | 'near'
    | 'selected'
    | null
  >(null);

  const [
    showTranslation,
    setShowTranslation,
  ] = useState(false);

  const startedAt = useRef(Date.now());

  const autoReadQuestions =
    useRef<Set<string>>(new Set());

  const spokenWrongResults =
    useRef<Set<string>>(new Set());

  const loadedLanguage = useRef(language);

  const examSessionVersion = 2;

  const storageKey =
    `lid.session.${mode}.${bundesland}`;

  const ui = getLidUi(language);

  const {
    speak,
    speakSequence,
    playStaticCue,
    stop,
    isSpeaking,
    rate,
    setRate,
    progress,
    currentText,
  } = useGermanSpeech();

  const learningLabels = (
    language === 'tr'
      ? {
          shared: 'ortak kelime',
          correct: 'doğru bağ',
          near: 'yakın kavram',
          selected: 'seçtiğin cevap',
          patterns: 'kalıp tanındı',
          patternRule: '2 doğru = tanındı',
          allAudio: 'Tüm sesler',
          legend: 'Öğrenme renkleri',
          why: 'Neden?',
          meaning: 'Anlam',
          word: 'Kelime',
          grammar: 'Gramer',
          eliminate: 'Eleme',
          showTranslation: 'Çeviriyi göster',
          hideTranslation: 'Çeviriyi gizle',
          questionTranslation: 'Soru çevirisi',
          grammarWeil:
            '"weil"den sonra çekimli fiil sona gider.',
          verb: 'fiil',
          sameFamilyWrong:
            'Aynı hak/özgürlük ailesinde, ama sorudaki anlamla eşleşmiyor.',
          structureOnly:
            'Cümle yapısı uyuyor; kavram sorunun anlamıyla eşleşmiyor.',
          unrelatedRights:
            'Soru bir hak/özgürlük gerekçesi arıyor; bu seçenek başka bir eylem veya durum söylüyor.',
          unrelated:
            'Bu seçenek sorunun anlamıyla eşleşmiyor.',
          freedomRoot: 'Freiheit = frei + -heit',
          freedomWord: 'Freiheit',
          sameStructure: 'Aynı yapı',
        }
      : language === 'de'
        ? {
            shared: 'gleiches Wort',
            correct: 'richtige Verbindung',
            near: 'ähnlicher Begriff',
            selected: 'deine Antwort',
            patterns: 'Muster erkannt',
            patternRule: '2 richtig = erkannt',
            allAudio: 'Alle Audios',
            legend: 'Lernfarben',
            why: 'Warum?',
            meaning: 'Bedeutung',
            word: 'Wort',
            grammar: 'Grammatik',
            eliminate: 'Ausschließen',
            showTranslation: 'Übersetzung zeigen',
            hideTranslation: 'Übersetzung ausblenden',
            questionTranslation: 'Frageübersetzung',
            grammarWeil:
              'Nach "weil" steht das konjugierte Verb am Ende.',
            verb: 'Verb',
            sameFamilyWrong:
              'Der Begriff gehört zur gleichen Rechte-/Freiheitsfamilie, passt aber nicht zur Bedeutung der Frage.',
            structureOnly:
              'Die Satzstruktur passt, aber der Begriff passt nicht zur Frage.',
            unrelatedRights:
              'Die Frage sucht eine Begründung aus dem Bereich Rechte/Freiheiten; diese Antwort beschreibt eine andere Handlung oder Situation.',
            unrelated:
              'Diese Antwort passt nicht zum Bedeutungsfokus der Frage.',
            freedomRoot: 'Freiheit = frei + -heit',
            freedomWord: 'Freiheit',
            sameStructure: 'Gleiche Struktur',
          }
        : {
            shared: 'shared word',
            correct: 'correct link',
            near: 'near concept',
            selected: 'your answer',
            patterns: 'patterns recognized',
            patternRule: '2 correct = recognized',
            allAudio: 'All audio',
            legend: 'Learning colors',
            why: 'Why?',
            meaning: 'Meaning',
            word: 'Word',
            grammar: 'Grammar',
            eliminate: 'Eliminate',
            showTranslation: 'Show translation',
            hideTranslation: 'Hide translation',
            questionTranslation: 'Question translation',
            grammarWeil:
              'After "weil", the conjugated verb goes to the end.',
            verb: 'verb',
            sameFamilyWrong:
              'The term belongs to the same rights/freedom family, but it does not match the question.',
            structureOnly:
              'The sentence structure fits, but the concept does not match the question.',
            unrelatedRights:
              'The question needs a reason from the rights/freedom domain; this answer states another action or situation.',
            unrelated:
              'This answer does not match the semantic focus of the question.',
            freedomRoot: 'Freiheit = frei + -heit',
            freedomWord: 'Freiheit',
            sameStructure: 'Same structure',
          }
  );

  const speechLabels = (
    language === 'tr'
      ? {
          listen: 'Dinle',
          stop: 'Durdur',
          slow: 'Yavaş',
          normal: 'Normal',
          audio: 'Almanca ses',
          speed: 'Konuşma hızı',
        }
      : language === 'de'
        ? {
            listen: 'Anhören',
            stop: 'Stoppen',
            slow: 'Langsam',
            normal: 'Normal',
            audio: 'Deutsche Aussprache',
            speed: 'Sprechgeschwindigkeit',
          }
        : {
            listen: 'Listen',
            stop: 'Stop',
            slow: 'Slow',
            normal: 'Normal',
            audio: 'German audio',
            speed: 'Speech speed',
          }
  );


  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);


  useEffect(() => {
    setShowTranslation(false);
  }, [currentIndex]);


  useEffect(() => {
    if (
      mode === 'exam'
      || loading
      || questions.length === 0
    ) {
      return;
    }

    const question =
      questions[currentIndex];

    if (!question) {
      return;
    }

    const key =
      String(
        question.question_key
        || question.id
      );

    if (
      autoReadQuestions.current.has(key)
    ) {
      return;
    }

    autoReadQuestions.current.add(key);

    // Let the newly rendered question settle first.
    const timer =
      window.setTimeout(() => {
        speak(
          question.canonical.text,
          'de-DE',
          {
            staticUrl:
              lidStaticAudioUrl(
                question.question_key,
                'question',
              ),
            fallback: true,
          },
        );
      }, 220);

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    currentIndex,
    loading,
    mode,
    questions,
    speak,
  ]);


  useEffect(() => {
    if (
      mode === 'exam'
      || questions.length === 0
    ) {
      return;
    }

    const question =
      questions[currentIndex];

    if (!question) {
      return;
    }

    const result =
      results[question.id];

    if (
      !result
      || !result.correct
    ) {
      return;
    }

    playStaticCue(
      '/lid-audio/de/feedback/richtig-chime.opus',
    );
  }, [
    currentIndex,
    mode,
    questions,
    results,
    playStaticCue,
  ]);


  useEffect(() => {
    if (
      mode === 'exam'
      || questions.length === 0
    ) {
      return;
    }

    const question =
      questions[currentIndex];

    if (!question) {
      return;
    }

    const result =
      results[question.id];

    if (
      !result
      || result.correct
      || !result.correct_option
    ) {
      return;
    }

    const marker =
      String(
        result.attempt_id
        || `${question.id}:${result.correct_option}`
      );

    if (
      spokenWrongResults.current.has(marker)
    ) {
      return;
    }

    const correctAnswer =
      question.canonical.options.find(
        option =>
          option.key
          === result.correct_option
      )?.text;

    if (
      !correctAnswer
      || isImageNumberOption(
        question.question_key,
        correctAnswer,
      )
    ) {
      return;
    }

    spokenWrongResults.current.add(marker);

    speakSequence([
      {
        text: 'Falsch.',
        lang: 'de-DE',
        staticUrl:
          '/lid-audio/de/feedback/falsch.opus',
        fallback: true,
      },
      {
        text: 'Richtige Antwort.',
        lang: 'de-DE',
        staticUrl:
          '/lid-audio/de/feedback/richtige-antwort.opus',
        fallback: true,
      },
      {
        text: correctAnswer,
        lang: 'de-DE',
        staticUrl:
          lidStaticAudioUrl(
            question.question_key,
            result.correct_option as
              | 'A'
              | 'B'
              | 'C'
              | 'D',
          ),
        fallback: true,
      },
    ]);
  }, [
    currentIndex,
    mode,
    questions,
    results,
    speakSequence,
  ]);


  useEffect(() => {
    const refreshPatternCount = () => {
      setRecognizedPatterns(
        countRecognizedPatterns()
      );
    };

    window.addEventListener(
      'lid-pattern-mastery-changed',
      refreshPatternCount,
    );

    window.addEventListener(
      'storage',
      refreshPatternCount,
    );

    return () => {
      window.removeEventListener(
        'lid-pattern-mastery-changed',
        refreshPatternCount,
      );

      window.removeEventListener(
        'storage',
        refreshPatternCount,
      );
    };
  }, []);


  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setExamFinished(false);

      try {
        if (mode === 'exam') {
          const saved =
            localStorage.getItem(storageKey);

          if (saved) {
            try {
              const parsed = JSON.parse(saved);

              if (
                parsed.version === examSessionVersion
                && Array.isArray(parsed.questions)
                && parsed.questions.length === 33
              ) {
                if (!cancelled) {
                  setQuestions(parsed.questions);
                  setCurrentIndex(
                    parsed.currentIndex || 0
                  );
                  setAnswers(
                    parsed.answers || {}
                  );
                  setResults(
                    parsed.results || {}
                  );
                  startedAt.current = Date.now();
                }

                return;
              }

              localStorage.removeItem(
                storageKey
              );
            } catch {
              localStorage.removeItem(
                storageKey
              );
            }
          }
        }

        const response = await fetch(
          `/api/v1/lid/questions?bundesland=${
            encodeURIComponent(bundesland)
          }&language=${
            encodeURIComponent(language)
          }&mode=${
            encodeURIComponent(mode)
          }`,
          {
            credentials: 'include',
          },
        );

        if (!response.ok) {
          if (response.status === 409) {
            try {
              const body = await response.json();

              if (
                body?.detail?.code
                === 'lid_exam_not_ready_for_state'
              ) {
                throw new Error(
                  `Die Prüfung ist für ${
                    body.detail.bundesland
                  } noch nicht freigegeben. `
                  + `Für diesen Modus fehlen noch `
                  + `verifizierte Landesfragen.`
                );
              }
            } catch (err) {
              if (
                err instanceof Error
                && !err.message.startsWith(
                  'Unexpected token'
                )
              ) {
                throw err;
              }
            }
          }

          const detail =
            await response.text();

          throw new Error(
            `Questions HTTP ${
              response.status
            }: ${detail}`
          );
        }

        const data =
          await response.json();

        if (!cancelled) {
          setQuestions(
            data.questions || []
          );
          setCurrentIndex(0);
          setAnswers({});
          setResults({});
          startedAt.current = Date.now();
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : String(err)
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    bundesland,
    mode,
    storageKey,
  ]);


  useEffect(() => {
    if (loadedLanguage.current === language) {
      return;
    }

    if (mode === 'exam') {
      loadedLanguage.current = language;
      return;
    }

    if (questions.length === 0) {
      return;
    }

    loadedLanguage.current = language;

    let cancelled = false;

    const refreshTranslations = async () => {
      try {
        const response = await fetch(
          `/api/v1/lid/questions?bundesland=${
            encodeURIComponent(bundesland)
          }&language=${
            encodeURIComponent(language)
          }&mode=practice`,
          { credentials: 'include' },
        );

        if (!response.ok) {
          throw new Error(
            `Questions HTTP ${response.status}`
          );
        }

        const data = await response.json();
        const byKey = new Map<string, Question>(
          (data.questions || []).map(
            (item: Question) =>
              [item.question_key, item] as const
          )
        );

        if (!cancelled) {
          setQuestions(previous =>
            previous.map(item => ({
              ...item,
              translation:
                byKey.get(item.question_key)?.translation
                || null,
            }))
          );
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : String(err)
          );
        }
      }
    };

    refreshTranslations();

    return () => {
      cancelled = true;
    };
  }, [bundesland, language, mode, questions.length]);


  useEffect(() => {
    if (
      mode !== 'exam'
      || questions.length !== 33
      || examFinished
    ) {
      return;
    }

    localStorage.setItem(
      storageKey,
      JSON.stringify({
        version: examSessionVersion,
        questions,
        currentIndex,
        answers,
        results,
      })
    );
  }, [
    mode,
    questions,
    currentIndex,
    answers,
    results,
    storageKey,
    examFinished,
  ]);


  const submitAnswer = async (
    question: Question,
    option: string,
  ) => {
    if (
      results[question.id]
      || submitting
    ) {
      return;
    }

    setAnswers(previous => ({
      ...previous,
      [question.id]: option,
    }));

    setSubmitting(true);
    setError(null);

    try {
      const durationMs = Math.max(
        0,
        Date.now() - startedAt.current
      );

      const response = await fetch(
        '/api/v1/lid/attempts',
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type':
              'application/json',
          },
          body: JSON.stringify({
            content_revision_id:
              question.id,
            selected_option: option,
            duration_ms: durationMs,
            mode,
            language,
          }),
        }
      );

      if (!response.ok) {
        const detail =
          await response.text();

        throw new Error(
          `Attempt HTTP ${
            response.status
          }: ${detail}`
        );
      }

      const result =
        await response.json();

      setResults(previous => ({
        ...previous,
        [question.id]: result,
      }));

      setLearningSeenCount(
        markLearningSeen(
          question.question_key
        )
      );

      onProgressChanged?.();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : String(err)
      );
    } finally {
      setSubmitting(false);
    }
  };


  const toggleFavorite = async (
    question: Question,
  ) => {
    const response = await fetch(
      `/api/v1/lid/favorites/${
        encodeURIComponent(question.id)
      }`,
      {
        method: question.favorite
          ? 'DELETE'
          : 'POST',
        credentials: 'include',
      }
    );

    if (!response.ok) {
      setError(
        `Favorite HTTP ${
          response.status
        }`
      );
      return;
    }

    const data =
      await response.json();

    setQuestions(previous =>
      previous.map(item =>
        item.id === question.id
          ? {
              ...item,
              favorite:
                Boolean(data.favorite),
            }
          : item
      )
    );

    onProgressChanged?.();
  };


  const move = (
    index: number
  ) => {
    stop();
    setCurrentIndex(index);
    startedAt.current = Date.now();
    setError(null);
  };


  const finishExam = () => {
    setExamFinished(true);
    localStorage.removeItem(
      storageKey
    );
  };


  if (loading) {
    return <LoadingLine />;
  }


  if (
    error
    && questions.length === 0
  ) {
    return (
      <StateText tone="critical">
        {error}
      </StateText>
    );
  }


  if (questions.length === 0) {
    return (
      <EmptyState
        title={
          mode === 'wrong'
            ? `0 · ${ui.mistakes}`
            : mode === 'favorites'
              ? `0 · ${ui.favorites}`
              : `0 · ${ui.questions}`
        }
        detail={`${ui.unavailable} · ${ui.learn}`}
      />
    );
  }


  if (
    mode === 'exam'
    && examFinished
  ) {
    const answered =
      Object.keys(results).length;

    const correct =
      Object.values(results)
        .filter(result =>
          result.correct
        ).length;

    return (
      <section className="lid-exam-result">
        <p className="lid-eyebrow">
          {ui.completed}
        </p>

        <h2>{ui.result}</h2>

        <div className="lid-exam-score">
          {correct} / 33
        </div>

        <p>
          {answered} / 33 · {ui.questions}
        </p>

        <p>
          {ui.accuracy}:{' '}
          <strong>
            {answered
              ? Math.round(
                  correct * 100 / answered
                )
              : 0}%
          </strong>
        </p>
      </section>
    );
  }


  const question =
    questions[currentIndex];

  const selected =
    answers[question.id];

  const result =
    results[question.id];

  const translation =
    question.translation;

  const correctKey =
    result?.correct_option
    || (result?.correct ? selected : null);

  const correctAnswer =
    question.canonical.options.find(
      option => option.key === correctKey
    )?.text || null;

  const translatedCorrectAnswer =
    translation?.options.find(
      option => option.key === correctKey
    )?.text || null;

  const selectedAnswer =
    question.canonical.options.find(
      option => option.key === selected
    )?.text || null;

  const translatedSelectedAnswer =
    translation?.options.find(
      option => option.key === selected
    )?.text || null;

  const learningModel =
    buildHumanLearningModel(
      question.canonical.text,
      translation?.text,
      language,
      question.canonical.options
        .slice(0, 4)
        .map(option => option.text),
    );

  const optionTexts =
    question.canonical.options
      .slice(0, 4)
      .map(option => option.text);

  const showLearningLegend = false;

  const showMicroBridge = false;

  /*
   * KNSAIX_LID_FULLTEXT_SEMANTIC_GERMAN_V1
   *
   * Semantic mappings are defined on the canonical German
   * question. Valid semantic spans can cross the visual A1
   * chunk boundaries, so translation-visible semantic mode
   * must use one full canonical range domain.
   *
   * When translation is not shown, the original A1 chunk
   * learning presentation remains unchanged.
   */
  const a1QuestionChunks =
    showTranslation
    && language !== 'de'
    && translation
      ? [
          question.canonical.text
        ]
      : buildA1QuestionChunks(
          question.canonical.text
        );

  const a1CorrectMorphology =
    freedomCompoundInsight(
      correctAnswer
    );

  const a1RelatedMorphologies =
    optionTexts
      .map(freedomCompoundInsight)
      .filter(
        (
          item,
        ): item is FreedomCompoundInsight =>
          Boolean(
            item
            && item.word
              !== a1CorrectMorphology?.word
          )
      )
      .filter(
        (item, index, items) =>
          items.findIndex(
            candidate =>
              candidate.word === item.word
          ) === index
      );

  const a1HasWeil =
    /\bweil\b/iu.test(
      question.canonical.text
    );

  const a1FinalVerb =
    finalGermanWord(correctAnswer);

  const nearDistractorKeys =
    result && correctAnswer
      ? question.canonical.options
          .slice(0, 4)
          .filter(
            option =>
              option.key !== correctKey
              && isNearWrongConcept(
                option.text,
                correctAnswer,
              )
          )
          .map(option => option.key)
      : [];


  const shPilotBackground =
    mode !== 'exam'
    && bundesland === 'SH'
      ? lidShPilotBackground(
          question.question_key
        )
      : null;

  return (
    <section className="lid-question-flow">
      <div className="lid-session-bar">
        <div className="lid-session-count">
          {mode === 'exam'
            ? (
              <span>
                {currentIndex + 1} / {questions.length}
              </span>
            )
            : (
              <button
                type="button"
                className="lid-question-jump"
                onClick={() => {
                  const raw = window.prompt(
                    language === 'tr'
                      ? `Soru numarası (1-${questions.length})`
                      : language === 'de'
                        ? `Fragenummer (1-${questions.length})`
                        : `Question number (1-${questions.length})`,
                    String(currentIndex + 1),
                  );

                  if (raw === null) {
                    return;
                  }

                  const target = Number.parseInt(
                    raw.trim(),
                    10,
                  );

                  if (
                    !Number.isInteger(target)
                    || target < 1
                    || target > questions.length
                  ) {
                    return;
                  }

                  move(target - 1);
                }}
                aria-label={
                  language === 'tr'
                    ? 'Soru numarasına git'
                    : language === 'de'
                      ? 'Zur Fragennummer springen'
                      : 'Go to question number'
                }
                title={
                  language === 'tr'
                    ? 'Soruyla doğrudan git'
                    : language === 'de'
                      ? 'Direkt zur Frage'
                      : 'Jump directly to question'
                }
              >
                {currentIndex + 1} / {questions.length}
              </button>
            )}

          {mode !== 'exam' && (
            <span className="lid-pattern-total">
              <span>
                {recognizedPatterns}{' '}
                {learningLabels.patterns}
              </span>

              <span
                className="lid-pattern-info"
                role="img"
                aria-label={
                  learningLabels.patternRule
                }
                title={
                  learningLabels.patternRule
                }
              >
                ⓘ
              </span>
            </span>
          )}
        </div>

        <div className="lid-progress-track">
          <div
            className="lid-progress-fill"
            style={{
              width: `${
                ((currentIndex + 1) / questions.length) * 100
              }%`,
            }}
          />
        </div>

      <button
        type="button"
        className="lid-icon-button"
          aria-label={
            question.favorite
            ? ui.removeFavorite
            : ui.saveFavorite
        }
        onClick={() =>
          toggleFavorite(question)
        }
      >
        {question.favorite
          ? '★'
          : '☆'}
      </button>
      </div>

      <article
        className={`lid-question-card ${
          shPilotBackground
            ? 'has-state-background '
            : ''
        }${
          activeLegend
            ? `is-legend-filtering legend-${activeLegend}`
            : ''
        }`}
      >
        {shPilotBackground && (
          <div
            className="lid-state-background"
            aria-hidden="true"
            style={{
              backgroundImage:
                `url("${shPilotBackground.url}")`,
              backgroundPosition:
                shPilotBackground.position,
            }}
          />
        )}

        <div className="lid-question-label">
          {ui.question} {currentIndex + 1}
        </div>

        {showLearningLegend && (
          <div
            className="lid-learning-legend"
            aria-label={learningLabels.legend}
          >
            {(
              [
                [
                  'shared',
                  learningLabels.shared,
                ],
                [
                  'correct',
                  learningLabels.correct,
                ],
                [
                  'near',
                  learningLabels.near,
                ],
                [
                  'selected',
                  learningLabels.selected,
                ],
              ] as const
            ).map(([kind, label]) => (
              <button
                key={kind}
                type="button"
                className={`is-${kind} ${
                  activeLegend === kind
                    ? 'is-active'
                    : ''
                }`}
                aria-pressed={
                  activeLegend === kind
                }
                onClick={() =>
                  setActiveLegend(
                    current =>
                      current === kind
                        ? null
                        : kind
                  )
                }
              >
                <i aria-hidden="true" />
                {label}
              </button>
            ))}
          </div>
        )}

        <div className="lid-question-focus">
          <h2
            className="lid-question-text lid-a1-question"
            lang="de"
          >
            {isSpeaking
              && currentText
                === question.canonical.text
              ? renderSpeechTrackedText(
                  question.canonical.text,
                  progress,
                )
              : a1QuestionChunks.map(
                  (chunk, index) => (
                    <span
                      key={`${chunk}-${index}`}
                      className="lid-a1-question-chunk"
                    >
                      {showTranslation
                        && language !== 'de'
                        && translation
                        ? renderQuestionGermanPairText(
                            chunk,
                            learningModel,
                            question.question_key,
                            language,
                          )
                        : result
                          ? chunk
                          : renderLearningText(
                              chunk,
                              learningModel,
                              {
                                questionFocus: null,
                              },
                            )}
                    </span>
                  )
                )}
          </h2>

          {mode !== 'exam'
            && language !== 'de'
            && translation
            && showTranslation
            && (
              <div
                className="lid-question-inline-translation"
                dir={translation.direction}
                lang={language}
              >
                <span
                  className="lid-question-inline-translation-label"
                >
                  {
                    learningLabels
                      .questionTranslation
                  }
                </span>

                <p>
                  {renderQuestionTranslationText(
                    translation.text,
                    learningModel,
                    question.question_key,
                    language,
                  )}
                </p>
              </div>
            )}

          <div
            className="lid-speech-controls"
            aria-label={speechLabels.audio}
          >
            <button
              type="button"
              className={`lid-speech-button lid-question-play-toggle ${
                isSpeaking ? 'is-active' : ''
              }`}
              onClick={() => {
                if (isSpeaking) {
                  stop();
                } else {
                  speak(
                    question.canonical.text,
                    'de-DE',
                    {
                      staticUrl:
                        lidStaticAudioUrl(
                          question.question_key,
                          'question',
                        ),
                      fallback: true,
                    },
                  );
                }
              }}
              aria-pressed={isSpeaking}
            
              aria-label={
                isSpeaking
                  ? speechLabels.stop
                  : speechLabels.listen
              }
            >
              <span
                className="lid-play-pause-glyph"
                aria-hidden="true"
              >
                {isSpeaking ? (
                  <span className="lid-pause-icon">
                    <i />
                    <i />
                  </span>
                ) : (
                  <span className="lid-play-icon" />
                )}
              </span>
</button>

            {mode !== 'exam'
              && language !== 'de'
              && translation
              && (
                <button
                  type="button"
                  className={`lid-question-translate-toggle lid-question-translate-toggle-inline ${
                    showTranslation
                      ? 'is-active'
                      : ''
                  }`}
                  onClick={() =>
                    setShowTranslation(
                      value => !value
                    )
                  }
                  aria-pressed={showTranslation}
                  aria-expanded={showTranslation}
                  aria-label={
                    showTranslation
                      ? learningLabels.hideTranslation
                      : learningLabels.showTranslation
                  }
                  title={
                    showTranslation
                      ? learningLabels.hideTranslation
                      : learningLabels.showTranslation
                  }
                >
                  <svg
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path
                      d="M4 5h9M8.5 3v2M6 8c1.2 2.4 3.1 4.3 5.5 5.5M11.5 8C10.2 11 7.9 13.3 5 15"
                    />
                    <path
                      d="M14 19l3.2-8 3.2 8M15.2 16h4"
                    />
                  </svg>
                </button>
              )}

            <div
              className="lid-speech-rate"
              role="group"
              aria-label={speechLabels.speed}
            >
              <span className="lid-speech-global-label">
                {learningLabels.allAudio}
              </span>

              {(
                [
                  'slow',
                  'normal',
                ] as SpeechRate[]
              ).map(value => (
                <button
                  key={value}
                  type="button"
                  className={
                    rate === value
                      ? 'is-active'
                      : ''
                  }
                  onClick={() => {
                    stop();
                    setRate(value);
                  }}
                  aria-pressed={
                    rate === value
                  }
                >
                  {value === 'slow'
                    ? speechLabels.slow
                    : speechLabels.normal}
                </button>
              ))}
            </div>
          </div>
        </div>

        {lidVisualUrl(question.question_key) && (
          <figure className="lid-question-visual">
            <img
              src={
                lidVisualUrl(question.question_key)
                ?? undefined
              }
              alt={question.canonical.text}
              decoding="async"
            />
          </figure>
        )}

        {translation && mode === 'exam' && (
          <aside
            dir={translation.direction}
            className="lid-translation"
          >
            <div className="lid-translation-meta">
              {translation.status === 'published'
                ? `${ui.translation} · ${ui.verified}`
                : `${ui.translation} · ${ui.pending}`}
            </div>
            <div style={{ display: 'none' }}>
            <StateText
              tone={
                translation.status
                === 'published'
                  ? 'positive'
                  : 'attention'
              }
            >
              {translation.status
                === 'published'
                  ? `${ui.translation} · ${ui.verified}`
                  : `${ui.translation} · ${ui.pending}`}
            </StateText>
            </div>

            <p>
              {translation.text}
            </p>
          </aside>
        )}

        {language !== 'de'
          && !translation && (
            <StateText tone="attention">
              {ui.translation} · {ui.unavailable}
              {' · '}Deutsch
            </StateText>
          )}

        <div
          className="lid-answer-list"
          role="radiogroup"
          aria-label={ui.answers}
        >
          {question.canonical.options.slice(0, 4).map(
            (canonicalOption, optionIndex) => {
              const translatedOption =
                translation?.options.find(
                  option =>
                    option.key
                    === canonicalOption.key
                );

              const isSelected =
                selected === canonicalOption.key;

              const isCorrect =
                Boolean(result)
                && canonicalOption.key
                  === correctKey;

              const isWrong =
                Boolean(result)
                && !result.correct
                && isSelected;

              const showSelected =
                isSelected
                && !result;

              const structureLabel =
                recurringAnswerStructure(
                  canonicalOption.text,
                  optionTexts,
                );

              const isNearDistractor =
                Boolean(result)
                && !isCorrect
                && isNearWrongConcept(
                  canonicalOption.text,
                  correctAnswer,
                );

              const nearDistractorIndex =
                nearDistractorKeys.indexOf(
                  canonicalOption.key
                );

              const nearDistractorTone =
                isNearDistractor
                  ? (
                      nearDistractorIndex % 2 === 0
                        ? 'a'
                        : 'b'
                    )
                  : null;

              return (
                <label
                  key={
                    canonicalOption.key
                  }
                  className={`lid-answer ${
                    showSelected ? 'is-selected' : ''
                  } ${
                    isCorrect ? 'is-correct' : ''
                  } ${
                    isWrong ? 'is-wrong' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name={
                      `q-${question.id}`
                    }
                    value={
                      canonicalOption.key
                    }
                    checked={
                      selected
                      === canonicalOption.key
                    }
                    disabled={
                      Boolean(result)
                      || submitting
                    }
                    onChange={() =>
                      submitAnswer(
                        question,
                        canonicalOption.key
                      )
                    }
                  />

                  <span className="lid-answer-key">
                    {canonicalOption.key}
                  </span>

                  <span className="lid-answer-body">
                    <span
                      className="lid-answer-main"
                      lang="de"
                    >
                      {structureLabel && (
                        <span
                          className={`lid-structure-badge ${
                            isCorrect
                              ? 'is-correct-structure'
                              : ''
                          }`}
                        >
                          <span aria-hidden="true">
                            {
                              isCorrect
                                ? '✓'
                                : result
                                  ? '✗'
                                  : '↳'
                            }
                          </span>
                          {structureLabel}
                        </span>
                      )}

                      {isSpeaking
                        && currentText
                          === canonicalOption.text
                        ? renderSpeechTrackedText(
                            canonicalOption.text,
                            progress,
                          )
                        : renderLearningText(
                            canonicalOption.text,
                            learningModel,
                            {
                              correctEvidence:
                                isCorrect
                                  ? (
                                      result
                                        ?.learning_bridge
                                        ?.answer_evidence
                                      || correctAnswer
                                    )
                                  : null,
                              distractorAnswer:
                                isNearDistractor
                                  ? canonicalOption.text
                                  : null,
                              distractorTone:
                                nearDistractorTone,
                            },
                          )}
                    </span>

                    {translatedOption
                      && showTranslation
                      && (
                      <span
                        dir={
                          translation?.direction
                        }
                        className="lid-answer-translation"
                      >
                        {translatedOption.text}
                      </span>
                    )}
                  </span>

                  {!isImageNumberOption(
                    question.question_key,
                    canonicalOption.text,
                  ) && (
                    <span
                      className="lid-answer-audio"
                      role="button"
                      tabIndex={0}
                      aria-label={
                        `${speechLabels.listen}: ${canonicalOption.text}`
                      }
                      onClick={event => {
                        event.preventDefault();
                        event.stopPropagation();
  
                        speak(
                          canonicalOption.text,
                          'de-DE',
                          {
                            staticUrl:
                              lidStaticAudioUrl(
                                question.question_key,
                                canonicalOption.key as
                                  | 'A'
                                  | 'B'
                                  | 'C'
                                  | 'D',
                              ),
                            fallback: false,
                          },
                        );
                      }}
                      onKeyDown={event => {
                        if (
                          event.key !== 'Enter'
                          && event.key !== ' '
                        ) {
                          return;
                        }
  
                        event.preventDefault();
                        event.stopPropagation();
  
                        speak(
                          canonicalOption.text,
                          'de-DE',
                          {
                            staticUrl:
                              lidStaticAudioUrl(
                                question.question_key,
                                canonicalOption.key as
                                  | 'A'
                                  | 'B'
                                  | 'C'
                                  | 'D',
                              ),
                            fallback: false,
                          },
                        );
                      }}
                    >
                      <span
                        className="lid-speaker-glyph"
                        aria-hidden="true"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          focusable="false"
                        >
                          <path
                            d="M4 9v6h4l5 4V5L8 9H4Z"
                          />
                          <path
                            d="M16 8.2a5 5 0 0 1 0 7.6M18.5 5.7a8.5 8.5 0 0 1 0 12.6"
                          />
                        </svg>
                      </span>
                    </span>
                  )}
                </label>
              );
            }
          )}
        </div>

        {mode !== 'exam'
          && result && (
            <section
              className={`lid-feedback ${
                result.correct
                  ? 'is-correct'
                  : 'is-wrong'
              }`}
              aria-live="polite"
              ref={node => {
                if (
                  !node
                  || node.dataset.autoScrolled === '1'
                  || !window.matchMedia(
                    '(max-width: 719px)',
                  ).matches
                ) {
                  return;
                }

                node.dataset.autoScrolled = '1';

                window.requestAnimationFrame(() => {
                  node.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                  });
                });
              }}
            >
              <header className="lid-feedback-head">
                <span className="lid-feedback-status">
                  {result.correct
                    ? `✓ ${ui.correct}`
                    : `✕ ${ui.incorrect}`}
                </span>
              </header>

              {!result.correct
                && selectedAnswer
                && (
                  <div className="lid-feedback-row is-selected-answer">
                    <small>{ui.incorrect}</small>

                    <span lang="de">
                      {selectedAnswer}
                    </span>

                    {translatedSelectedAnswer
                      && language !== 'de'
                      && showTranslation
                      && (
                        <span
                          className="lid-feedback-translation"
                          dir={translation?.direction}
                        >
                          {translatedSelectedAnswer}
                        </span>
                      )}
                  </div>
                )}

              {correctAnswer && (
                <div className="lid-feedback-row is-correct-answer">
                  <small>
                    {ui.correct}
                    {correctKey
                      ? ` · ${correctKey}`
                      : ''}
                  </small>

                  <strong lang="de">
                    {renderLearningText(
                      correctAnswer,
                      learningModel,
                      {
                        correctEvidence:
                          result.learning_bridge
                            ?.answer_evidence
                          || correctAnswer,
                      },
                    )}
                  </strong>

                  {translatedCorrectAnswer
                    && language !== 'de'
                    && showTranslation
                    && (
                      <span
                        className="lid-feedback-translation"
                        dir={translation?.direction}
                      >
                        {translatedCorrectAnswer}
                      </span>
                    )}
                </div>
              )}

              {result.learning_bridge?.reason && (
                <div className="lid-feedback-reason">
                  <small>
                    {learningLabels.why}
                  </small>

                  <p>
                    {result.learning_bridge.reason}
                  </p>
                </div>
              )}

              {learningModel.concept && (
                <div className="lid-feedback-memory">
                  <span className="lid-feedback-memory-icon">
                    <A1ConceptIcon
                      kind={learningModel.visual}
                    />
                  </span>

                  <div>
                    <small>
                      {learningLabels.meaning}
                    </small>

                    <strong>
                      {learningModel.concept}
                    </strong>
                  </div>
                </div>
              )}

              {learningModel.chunks.length > 1 && (
                <details className="lid-feedback-more">
                  <summary>
                    {learningLabels.word}
                  </summary>

                  <div className="lid-feedback-more-body">
                    {learningModel.chunks.map(
                      (chunk, index) => (
                        <div
                          className="lid-feedback-chunk"
                          key={`${chunk.german}-${index}`}
                        >
                          <strong lang="de">
                            {chunk.german}
                          </strong>

                          {chunk.translation
                            && language !== 'de'
                            && (
                              <span
                                lang={language}
                                dir={translation?.direction}
                              >
                                {chunk.translation}
                              </span>
                            )}
                        </div>
                      ),
                    )}
                  </div>
                </details>
              )}
            </section>
          )}

        {mode !== 'exam'
          && result
          && correctAnswer
          && (
            <section className="lid-a1-lesson">
              <header className="lid-a1-lesson-header">
                <span className="lid-a1-lesson-icon">
                  <A1ConceptIcon
                    kind={learningModel.visual}
                  />
                </span>

                <div>
                  <span>
                    {learningLabels.why}
                  </span>

                  <strong>
                    {
                      result.learning_bridge
                        ?.answer_evidence
                      || correctAnswer
                    }
                  </strong>
                </div>
              </header>

              <div className="lid-a1-lesson-grid">
                <article className="lid-a1-lesson-part">
                  <span className="lid-a1-step">
                    1
                  </span>

                  <div>
                    <small>
                      {learningLabels.meaning}
                    </small>

                    <div className="lid-a1-meaning-link">
                      <span>
                        {
                          result.learning_bridge
                            ?.question_focus
                          || question.canonical.text
                        }
                      </span>

                      <b aria-hidden="true">
                        →
                      </b>

                      <strong>
                        {
                          result.learning_bridge
                            ?.answer_evidence
                          || correctAnswer
                        }
                      </strong>
                    </div>

                    {result.learning_bridge
                      ?.reason && (
                      <p>
                        {
                          result.learning_bridge
                            .reason
                        }
                      </p>
                    )}
                  </div>
                </article>

                {a1CorrectMorphology && (
                  <article className="lid-a1-lesson-part">
                    <span className="lid-a1-step">
                      2
                    </span>

                    <div>
                      <small>
                        {learningLabels.word}
                      </small>

                      <strong
                        className="lid-a1-compound-title"
                        lang="de"
                      >
                        {a1CorrectMorphology.word}
                      </strong>

                      <div className="lid-a1-morphemes">
                        <span>
                          {a1CorrectMorphology.base}
                        </span>

                        {a1CorrectMorphology.linker && (
                          <>
                            <b>+</b>
                            <span>
                              {
                                a1CorrectMorphology
                                  .linker
                              }
                            </span>
                          </>
                        )}

                        <b>+</b>
                        <span>
                          {learningLabels.freedomWord}
                        </span>
                      </div>

                      <p>
                        {learningLabels.freedomRoot}
                      </p>

                      {a1RelatedMorphologies
                        .slice(0, 2)
                        .map(item => (
                          <div
                            className="lid-a1-related-word"
                            key={item.word}
                          >
                            <small>
                              {
                                learningLabels
                                  .sameStructure
                              }
                            </small>

                            <span lang="de">
                              {item.word}
                              {' = '}
                              {item.base}
                              {item.linker
                                ? ` + ${item.linker}`
                                : ''}
                              {' + '}
                              {learningLabels.freedomWord}
                            </span>
                          </div>
                        ))}
                    </div>
                  </article>
                )}

                {a1HasWeil && (
                  <article className="lid-a1-lesson-part">
                    <span className="lid-a1-step">
                      3
                    </span>

                    <div>
                      <small>
                        {learningLabels.grammar}
                      </small>

                      <strong>
                        {learningLabels.grammarWeil}
                      </strong>

                      <div
                        className="lid-a1-grammar-example"
                        lang="de"
                      >
                        <span>weil</span>

                        <span>
                          {correctAnswer}
                        </span>

                        {a1FinalVerb && (
                          <mark>
                            {a1FinalVerb}

                            <small>
                              {
                                learningLabels
                                  .verb
                              }
                            </small>
                          </mark>
                        )}
                      </div>
                    </div>
                  </article>
                )}

                {!result.correct && (
                  <article className="lid-a1-lesson-part lid-a1-elimination">
                    <span className="lid-a1-step">
                      {a1HasWeil ? '4' : '3'}
                    </span>

                    <div>
                      <small>
                        {learningLabels.eliminate}
                      </small>

                      <div className="lid-a1-elimination-list">
                        {question.canonical.options
                          .slice(0, 4)
                          .filter(
                            option =>
                              option.key
                              !== correctKey
                          )
                          .map(option => {
                            const kind =
                              a1EliminationKind(
                                option.text,
                                correctAnswer,
                                optionTexts,
                              );

                            const explanation =
                              kind === 'near'
                                ? learningLabels
                                    .sameFamilyWrong
                                : kind === 'structure'
                                  ? learningLabels
                                      .structureOnly
                                  : kind
                                      === 'unrelated-rights'
                                    ? learningLabels
                                        .unrelatedRights
                                    : learningLabels
                                        .unrelated;

                            return (
                              <div
                                key={option.key}
                                className="lid-a1-elimination-item"
                              >
                                <span>
                                  {option.key}
                                </span>

                                <div>
                                  <strong lang="de">
                                    {option.text}
                                  </strong>

                                  <p>
                                    {explanation}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </article>
                )}
              </div>
            </section>
          )}

        {mode !== 'exam' && (
          <div
            className="lid-v9-mastery-runtime"
            aria-hidden="true"
          >
            <LiDLearningBridge
            language={language}
            questionKey={question.question_key}
            question={question.canonical.text}
            translatedQuestion={translation?.text}
            translationDirection={translation?.direction}
            selectedKey={selected}
            selectedAnswer={selectedAnswer}
            translatedSelectedAnswer={
              translatedSelectedAnswer
            }
            correctKey={correctKey}
            correctAnswer={correctAnswer}
            translatedCorrectAnswer={
              translatedCorrectAnswer
            }
            answerConnection={
              result?.learning_bridge
            }
            correct={result?.correct}
            attemptId={result?.attempt_id}
            correctLabel={ui.correct}
            incorrectLabel={ui.incorrect}
            options={
              question.canonical.options
                .slice(0, 4)
                .map(option => option.text)
            }
          />
          </div>
        )}
      </article>

      {error && (
        <StateText tone="critical">
          {error}
        </StateText>
      )}

      <div className="lid-navigation">
        <button
          type="button"
          className="lid-secondary-button"
          onClick={() =>
            move(
              Math.max(
                currentIndex - 1,
                0
              )
            )
          }
          disabled={
            currentIndex === 0
          }
        >
          <span
            className="lid-nav-arrows"
            aria-hidden="true"
          >
            ‹‹‹
          </span>
          <span>Zurück</span>
        </button>

        {mode !== 'exam'
          && (
            <button
              type="button"
              className={`lid-question-translate-toggle lid-navigation-translate-toggle ${
                showTranslation
                  ? 'is-active'
                  : ''
              }`}
              onClick={() => {
                if (!translation) {
                  return;
                }

                setShowTranslation(
                  value => !value
                );
              }}
              disabled={!translation}
              aria-disabled={!translation}
              aria-pressed={showTranslation}
              aria-expanded={showTranslation}
              aria-label={
                translation
                  ? (
                      showTranslation
                        ? learningLabels.hideTranslation
                        : learningLabels.showTranslation
                    )
                  : learningLabels.showTranslation
              }
              title={
                translation
                  ? (
                      showTranslation
                        ? learningLabels.hideTranslation
                        : learningLabels.showTranslation
                    )
                  : learningLabels.showTranslation
              }
            >
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M4 5h9M8.5 3v2M6 8c1.2 2.4 3.1 4.3 5.5 5.5M11.5 8C10.2 11 7.9 13.3 5 15"
                />
                <path
                  d="M14 19l3.2-8 3.2 8M15.2 16h4"
                />
              </svg>
            </button>
          )}

        {mode === 'exam'
          && currentIndex
             === questions.length - 1
          ? (
            <button
              type="button"
              className="lid-primary-button"
              onClick={finishExam}
            >
              {ui.finish}
            </button>
          )
          : (
            <button
              type="button"
              className="lid-primary-button"
              onClick={() =>
                move(
                  Math.min(
                    currentIndex + 1,
                    questions.length - 1
                  )
                )
              }
              disabled={
                currentIndex
                === questions.length - 1
              }
            >
              <span>Weiter</span>
              <span
                className="lid-nav-arrows"
                aria-hidden="true"
              >
                ›››
              </span>
            </button>
          )}
      </div>
    </section>
  );
}
